/**
 * URL helpers for OG / link preview in the composer (`social-vue-ui` `URL_UTILS` + completion rules).
 *
 * Twitter/X **status** URLs: keep in sync with `extractTwitterFromText` in `postUtils.ts` — Microlink
 * cannot render real tweet cards (X returns an error interstitial to scrapers), so callers should
 * use `parseTwitterXStatusUrl` / `isTwitterXStatusUrl` and avoid treating Microlink output as truth.
 */

/** Host + `/user/status/id` — keep in sync with embed detection in postUtils. `\b` allows `…/123?s=01`. */
const TWITTER_X_STATUS_URL =
  /^https?:\/\/(?:www\.|m\.|mobile\.)?(?:twitter\.com|x\.com)\/([a-zA-Z0-9_]+)\/status\/(\d+)\b/i;

const URL_PATTERN =
  /(?<![\w@])(?:https?:\/\/)?(?:www\.)?[a-z0-9][-a-z0-9.]*\.[a-z]{2,}(?:\/[^\s]*)?/gi;

export function extractAllUrls(text: string | null | undefined): string[] {
  if (!text) return [];
  const matches = text.match(URL_PATTERN);
  if (!matches?.length) return [];
  return matches.map((url) =>
    url.startsWith("http") ? url : `https://${url.trim()}`,
  );
}

export function normalizeForCache(url: string): string {
  return normalizeForComparison(url);
}

export function normalizeForComparison(url: string): string {
  let normalized = url.trim().toLowerCase();
  normalized = normalized.replace(/^https?:\/\//, "");
  normalized = normalized.replace(/^www\./, "");
  normalized = normalized.replace(/^x\.com\//, "twitter.com/");
  return normalized.replace(/\/$/, "");
}

export function findAvailableUrlsForReplacement(
  text: string,
  previewUrl: string,
): string[] {
  const allUrls = extractAllUrls(text);
  if (!previewUrl || allUrls.length === 0) return allUrls;

  const normalizedPreview = normalizeForComparison(previewUrl);
  const seen = new Set<string>();
  const out: string[] = [];

  for (const url of allUrls) {
    const n = normalizeForComparison(url);
    if (n === normalizedPreview) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(url);
  }
  return out;
}

export function isTwitterOrXUrl(url: string): boolean {
  return /^https?:\/\/(?:www\.|m\.|mobile\.)?(?:twitter\.com|x\.com)(?:\/|$)/i.test(
    url.trim(),
  );
}

export function parseTwitterXStatusUrl(
  url: string,
): { username: string; tweetId: string } | null {
  const m = url.trim().match(TWITTER_X_STATUS_URL);
  if (!m) return null;
  return { username: m[1], tweetId: m[2] };
}

export function isTwitterXStatusUrl(url: string): boolean {
  return parseTwitterXStatusUrl(url) !== null;
}

/**
 * True when the last URL in the body looks finished enough to fetch metadata (debounced).
 */
export function isCompletedUrl(text: string | null | undefined): boolean {
  if (!text) return false;

  if (text.endsWith(" ") || text.endsWith("\n")) {
    return true;
  }

  const match = text.match(URL_PATTERN);
  if (!match) return false;

  const url = match[0];
  const urlPosition = text.indexOf(url);

  if (urlPosition + url.length < text.length) {
    const afterUrl = text.substring(urlPosition + url.length);
    if (
      afterUrl[0] === " " ||
      afterUrl[0] === "\n" ||
      afterUrl.charCodeAt(0) === 10
    ) {
      return true;
    }
    if (afterUrl.includes("\n")) {
      return true;
    }
  }

  if (text.trim() !== url && !text.trim().endsWith(url)) {
    return true;
  }

  if (url.match(/https?:\/\/(?:www\.|m\.|mobile\.)?(twitter\.com|x\.com)/i)) {
    if (
      url.match(
        /https?:\/\/(?:www\.|m\.|mobile\.)?(twitter\.com|x\.com)\/[a-z0-9_]{4,}/i,
      )
    ) {
      return true;
    }
    return false;
  }

  if (url.match(/https?:\/\/(youtube\.com|youtu\.be)/i)) {
    if (
      url.includes("youtube.com/watch?v=") ||
      url.includes("youtube.com/live/") ||
      url.includes("youtube.com/embed/")
    ) {
      return true;
    }
    if (url.match(/https?:\/\/youtu\.be\/[a-zA-Z0-9_-]{5,}/i)) {
      return true;
    }
    return false;
  }

  if (/\.(com|org|net|edu|gov|io|co|ai|app|dev)\b/i.test(url)) {
    return true;
  }

  if (/\.[a-z]{2,}\//.test(url)) {
    return true;
  }

  return false;
}

/**
 * social-vue-ui `URL_UTILS.formatForDisplay`: no protocol/www; show x.com for Twitter host.
 */
export function formatUrlForDisplay(url: string | null | undefined): string {
  if (!url) return "";
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    let host = u.hostname.replace(/^www\./, "");
    const path = u.pathname.replace(/\/$/, "") || "";
    if (host.toLowerCase() === "twitter.com") host = "x.com";
    return `${host}${path}` || url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  } catch {
    let s = url.replace(/^https?:\/\//, "").replace(/^www\./, "");
    s = s.replace(/^twitter\.com\//, "x.com/");
    return s.replace(/\/$/, "");
  }
}
